import {
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import LoadingButton from "../../components/LoadingButton";
import { hostname, parseError } from "../../utils";

interface Audience {
  id: string;
  audienceId: string;
  audienceName: string;
  listId: string;
  listName: string | null;
  companyId: string;
  companyName: string | null;
  ownerEmail: string | null;
  ownerName: string | null;
  ownerUid: string | null;
  refreshInterval: string | null;
  maxRecordsPerRefresh: number | null;
  lastRefreshDate: string | null;
  emailNotificationEnabled: boolean;
  emailNotificationRecipients: string[] | null;
  createdDate: string | null;
}

interface AudienceStats {
  totalAudiences: number;
  refreshingAudiences: number;
  uniqueCompanies: number;
  withEmailNotifications: number;
  intervalBreakdown: Record<string, number>;
}

interface AudienceManagementProps {
  getToken: () => Promise<string>;
}

const REFRESH_INTERVAL_OPTIONS = [
  { value: "", label: "All" },
  { value: "daily", label: "Daily" },
  { value: "3days", label: "Every 3 Days" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "none", label: "No Refresh" },
];

const AudienceManagement: React.FC<AudienceManagementProps> = ({
  getToken,
}) => {
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [stats, setStats] = useState<AudienceStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Search and filter
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );
  const [refreshIntervalFilter, setRefreshIntervalFilter] = useState("");

  // Copy ID feedback
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [audienceToDelete, setAudienceToDelete] = useState<Audience | null>(
    null
  );
  const [deleting, setDeleting] = useState(false);

  const fetchAudiences = async (search?: string) => {
    setLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const params: Record<string, string | number> = {
        page: page + 1,
        perPage: rowsPerPage,
      };

      if (search) {
        params.search = search;
      }

      if (refreshIntervalFilter) {
        params.refreshInterval = refreshIntervalFilter;
      }

      const response = await axios.get(
        `${hostname}/integrations/audience/admin`,
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setAudiences(response.data.audiences);
      setTotalCount(response.data.pagination.total);
    } catch (err) {
      setError(parseError(err));
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setLoadingStats(true);

    try {
      const token = await getToken();
      const response = await axios.get(
        `${hostname}/integrations/audience/admin/stats`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setStats(response.data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchAudiences(searchQuery);
    fetchStats();
  }, [page, rowsPerPage, refreshIntervalFilter]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);

    // Debounce search
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setPage(0);
      fetchAudiences(value);
    }, 300);

    setSearchTimeout(timeout);
  };

  const handleRefreshIntervalChange = (value: string) => {
    setRefreshIntervalFilter(value);
    setPage(0);
  };

  const handleDeleteAudience = (audience: Audience) => {
    setAudienceToDelete(audience);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!audienceToDelete) return;

    setDeleting(true);
    setError(null);

    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/integrations/audience/admin/${audienceToDelete.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSuccess(
        `Audience "${audienceToDelete.audienceName}" deleted successfully`
      );
      setDeleteDialogOpen(false);
      setAudienceToDelete(null);
      fetchAudiences(searchQuery);
      fetchStats();
    } catch (err) {
      setError(parseError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return moment(dateString).format("MMM D, YYYY h:mm A");
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return "Unknown";
    return moment(dateString).format("MMM D, YYYY");
  };

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const getRefreshIntervalLabel = (interval: string | null) => {
    switch (interval) {
      case "daily":
        return "Daily";
      case "3days":
        return "3 Days";
      case "weekly":
        return "Weekly";
      case "monthly":
        return "Monthly";
      case "none":
      case null:
        return "None";
      default:
        return interval;
    }
  };

  const getRefreshIntervalColor = (
    interval: string | null
  ): "primary" | "info" | "warning" | "default" => {
    switch (interval) {
      case "daily":
        return "primary";
      case "3days":
        return "info";
      case "weekly":
        return "warning";
      case "monthly":
        return "default";
      default:
        return "default";
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        Audience Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {stats.totalAudiences}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Audiences
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {stats.refreshingAudiences}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Auto-Refreshing
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {stats.uniqueCompanies}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Companies
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {stats.withEmailNotifications}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                With Notifications
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Refresh Interval Breakdown */}
      {stats && stats.intervalBreakdown && (
        <Box sx={{ mb: 3, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
            By Interval:
          </Typography>
          {Object.entries(stats.intervalBreakdown).map(([interval, count]) => (
            <Chip
              key={interval}
              label={`${getRefreshIntervalLabel(interval)}: ${count}`}
              size="small"
              variant="outlined"
              color={getRefreshIntervalColor(interval)}
            />
          ))}
        </Box>
      )}

      {/* Search and Filter */}
      <Box sx={{ mb: 3, display: "flex", gap: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search by company, audience, or list name..."
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Refresh Interval</InputLabel>
          <Select
            value={refreshIntervalFilter}
            onChange={(e) => handleRefreshIntervalChange(e.target.value)}
            label="Refresh Interval"
          >
            {REFRESH_INTERVAL_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Audiences Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>List / Audience</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Refresh</TableCell>
                <TableCell>Last Refresh</TableCell>
                <TableCell>Notify</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {audiences.map((audience) => (
                <TableRow key={audience.id} hover>
                  <TableCell>
                    <Tooltip
                      title={
                        copiedId === audience.companyId
                          ? "Copied!"
                          : `Click to copy: ${audience.companyId}`
                      }
                      placement="top"
                    >
                      <Box
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "primary.main" },
                        }}
                        onClick={() => handleCopyId(audience.companyId)}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {audience.companyName || "Unnamed"}
                        </Typography>
                      </Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>
                        {audience.listName || "Unknown List"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {audience.audienceName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {audience.ownerName || "No name"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {audience.ownerEmail || audience.ownerUid || "-"}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {audience.refreshInterval &&
                      audience.refreshInterval !== "none" ? (
                        <>
                          <Chip
                            icon={<RefreshIcon sx={{ fontSize: 14 }} />}
                            label={getRefreshIntervalLabel(
                              audience.refreshInterval
                            )}
                            size="small"
                            color={getRefreshIntervalColor(
                              audience.refreshInterval
                            )}
                          />
                          {audience.maxRecordsPerRefresh && (
                            <Typography variant="caption" color="text.secondary">
                              max {audience.maxRecordsPerRefresh}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(audience.lastRefreshDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {audience.emailNotificationEnabled ? (
                      <Tooltip
                        title={
                          audience.emailNotificationRecipients?.join(", ") ||
                          "No recipients"
                        }
                      >
                        <EmailIcon color="primary" fontSize="small" />
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDateShort(audience.createdDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Delete audience">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteAudience(audience)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}

              {audiences.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No audiences found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            Delete Audience
            <IconButton
              onClick={() => setDeleteDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {audienceToDelete && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This will delete the audience and stop any automatic refreshes.
              </Alert>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Are you sure you want to delete this audience?
              </Typography>
              <Box
                sx={{
                  backgroundColor: "action.hover",
                  p: 2,
                  borderRadius: 1,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {audienceToDelete.audienceName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Company: {audienceToDelete.companyName || "Unknown"}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  List: {audienceToDelete.listName || "Unknown"}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Owner: {audienceToDelete.ownerEmail || "Unknown"}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <LoadingButton onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="error"
            onClick={confirmDelete}
            loading={deleting}
          >
            Delete Audience
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AudienceManagement;
